import React from 'react';
import momentTimezone from 'moment-timezone';
import {
  CalendarOutlined,
  CreditCardOutlined,
  IdcardOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import ActionBar from '../../../components/common/ActionBar';
import ClinicianCard from '../../../components/common/ClinicianCard';
import ContactHelpText from '../../../components/common/ContactHelpText';
import DetailRow from '../../../components/common/DetailRow';
import InlineAlert from '../../../components/common/InlineAlert';
import KeyTerm from '../../../components/common/KeyTerm';
import Note from '../../../components/common/Note';
import {
  Card,
  CardBody,
  PageHead,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../../components/common/Card';
import { PAYMENT_STATE_LABEL } from '../../../constants/rescheduleConstants';
import {
  formatAppointmentDateTimeInZone,
  formatAppointmentMomentLabel,
} from '../../../utils/appointmentDisplayUtils';
import {
  PAYMENT_STATE,
  isInsideCancellationWindow,
} from '../../../utils/rescheduleUtils';
import {
  Compare,
  CompareArrow,
  CompareLabel,
  CompareSide,
  CompareValue,
} from './styles';

const ConfirmNewTime = ({
  workflow,
  contact,
  newStart,
  timezone,
  durationMinutes,
  paymentState,
  isCoverChanged,
  isSubmitting,
  errorMessage,
  onBack,
  onConfirm,
}) => {
  const { appointment, specialist } = workflow || {};
  const newLabel = formatAppointmentMomentLabel(
    momentTimezone(newStart).tz(timezone)
  );

  return (
    <>
      <PageHead>
        <PageTitle>Confirm your new time</PageTitle>
        <PageSubtitle>Nothing changes until you tap Confirm.</PageSubtitle>
      </PageHead>

      <Card>
        <CardBody>
          <Compare>
            <CompareSide>
              <CompareLabel>Now</CompareLabel>
              <CompareValue struck>
                {formatAppointmentDateTimeInZone(
                  appointment?.startDateTime,
                  timezone
                )}
              </CompareValue>
            </CompareSide>
            <CompareArrow aria-hidden>&rarr;</CompareArrow>
            <CompareSide highlight>
              <CompareLabel>New</CompareLabel>
              <CompareValue>{newLabel}</CompareValue>
            </CompareSide>
          </Compare>
          <ClinicianCard specialist={specialist} />
          <DetailRow
            label="Appointment length"
            value={`${durationMinutes} minutes`}
          />
          <DetailRow label="Timezone" value={timezone} />
          <DetailRow
            label="Payment"
            value={PAYMENT_STATE_LABEL[paymentState]}
          />
        </CardBody>
      </Card>

      {isInsideCancellationWindow(newStart) && (
        <Note tone="warning">
          The time you have chosen is less than 2 business days away. Once you
          confirm it, missed appointments, or appointments cancelled without
          valid medical reason within 2 business days of the appointment will
          incur a fee ($150).
        </Note>
      )}

      <Card>
        <CardBody>
          <SectionTitle>What happens after you confirm</SectionTitle>
          <KeyTerm icon={<CalendarOutlined />}>
            Your appointment moves to the new time. Your clinician stays the
            same.
          </KeyTerm>
          <KeyTerm icon={<MessageOutlined />}>
            You will receive a new confirmation SMS and email with the updated
            time. This page will show the new time too.
          </KeyTerm>
          {paymentState === PAYMENT_STATE.UNPAID && (
            <KeyTerm icon={<CreditCardOutlined />}>
              <b>Your payment is still outstanding.</b> That SMS will include
              your payment link &mdash; please complete the payment to secure
              the appointment.
            </KeyTerm>
          )}
          {paymentState === PAYMENT_STATE.MANUAL && (
            <KeyTerm icon={<CreditCardOutlined />}>
              Nothing to pay online. Our team will contact you to arrange
              payment for this appointment.
            </KeyTerm>
          )}
          {paymentState === PAYMENT_STATE.PAID && (
            <KeyTerm icon={<CreditCardOutlined />}>
              You have already paid for this appointment. Your payment carries
              over and you will not be charged again.
            </KeyTerm>
          )}
          {isCoverChanged && (
            <KeyTerm icon={<IdcardOutlined />}>
              The card details you entered are saved to your record.
            </KeyTerm>
          )}
        </CardBody>
      </Card>

      {errorMessage && <InlineAlert>{errorMessage}</InlineAlert>}

      <ContactHelpText phone={contact.phone} />

      <ActionBar
        backLabel="Back"
        // No going back while the change is in flight: it may still go through.
        onBack={isSubmitting ? undefined : onBack}
        nextLabel="Confirm new time"
        onNext={onConfirm}
        isLoading={isSubmitting}
      />
    </>
  );
};

export default ConfirmNewTime;
